import { ulid } from '../ulid.js';
import { texteBrut } from '../linkify.js';
import type { Task } from '../../api/tasks.js';
import type { CreateCourseInput } from '../../api/courses.js';
import type { Habit } from '../../api/habits.js';

/**
 * La file des écritures faites hors-ligne, en attente de rejeu.
 * Décisions et alternatives rejetées : [ADR 0010](../../../../docs/adr/0010-ecritures-offline-file-de-rejeu.md).
 *
 * Ce module est volontairement sans état React et sans I/O au-delà de `localStorage` :
 * tout ce qui décide quelque chose (repli, remap, péremption) est une fonction pure,
 * testée dans `tests/offline-queue.test.ts` sous l'environnement `node` de vitest —
 * qui n'a pas de `localStorage`.
 */

export const TMP_PREFIX = 'tmp_';
const STORAGE_KEY = 'nota_offline_queue';

/** § 4 de l'ADR : au-delà, l'état local qui a produit l'entrée n'existe plus. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type TaskPatch = Partial<Pick<Task, 'title' | 'description' | 'projectId' | 'dueDate' | 'rrule' | 'subtasks'>>;
export type CoursePatch = { title?: string; done?: boolean; qty?: number };
export type HabitPatch = Partial<Omit<Habit, 'habitId' | 'createdAt'>>;

export type CreateTaskInput = {
  title: string;
  projectId: string;
  description?: string | null;
  dueDate?: string | null;
  rrule?: string | null;
  subtasks?: Array<{ title: string }>;
};

export type CreateHabitInput = { name: string; emoji?: string; color?: string; order?: number };

export type QueuedOp =
  | { kind: 'task.create'; id: string; input: CreateTaskInput }
  | { kind: 'task.update'; id: string; patch: TaskPatch }
  | { kind: 'task.reschedule'; id: string; dueDate: string | null }
  | { kind: 'task.complete'; id: string }
  | { kind: 'task.delete'; id: string }
  | { kind: 'course.create'; id: string; input: CreateCourseInput }
  | { kind: 'course.update'; id: string; patch: CoursePatch }
  | { kind: 'course.delete'; id: string }
  | { kind: 'habit.create'; id: string; input: CreateHabitInput }
  | { kind: 'habit.update'; id: string; patch: HabitPatch }
  | { kind: 'habit.delete'; id: string }
  | { kind: 'habit.log'; id: string; date: string }
  | { kind: 'habit.unlog'; id: string; date: string };

export interface QueueEntry {
  entryId: string;
  at: number;
  op: QueuedOp;
}

/** Ce qu'on montre dans le toast quand une entrée est jetée. */
export function describeOp(op: QueuedOp): string {
  switch (op.kind) {
    case 'task.create':   return `Task « ${texteBrut(op.input.title)} »`;
    case 'task.update':   return 'Task edit';
    case 'task.reschedule': return 'Task reschedule';
    case 'task.complete': return 'Task completion';
    case 'task.delete':   return 'Task deletion';
    case 'course.create': return `Course « ${op.input.title ?? 'item'} »`;
    case 'course.update': return 'Course edit';
    case 'course.delete': return 'Course deletion';
    case 'habit.create':  return `Habit « ${op.input.name} »`;
    case 'habit.update':  return 'Habit edit';
    case 'habit.delete':  return 'Habit deletion';
    case 'habit.log':     return `Habit check (${op.date})`;
    case 'habit.unlog':   return `Habit uncheck (${op.date})`;
  }
}

export function isTempId(id: string): boolean {
  return id.startsWith(TMP_PREFIX);
}

export function newTempId(): string {
  return TMP_PREFIX + ulid();
}

const CREATE_KINDS = new Set(['task.create', 'course.create', 'habit.create']);
const DELETE_KINDS = new Set(['task.delete', 'course.delete', 'habit.delete']);

function domain(kind: QueuedOp['kind']): string {
  return kind.split('.')[0]!;
}

/**
 * La clé sous laquelle deux opérations se replient, ou `null` si l'opération ne se
 * replie jamais. Deux entrées de même clé sont telles que rejouer les deux revient à
 * rejouer la seconde.
 *
 * `habit.log` et `habit.unlog` partagent une clé alors que leurs `kind` diffèrent :
 * ce sont les deux faces d'un même interrupteur, et cocher puis décocher au même
 * endroit ne doit pas coûter deux requêtes au retour du réseau. Les comparer par
 * `kind` laissait justement ce cas s'empiler.
 *
 * `complete` n'a pas de clé : sur une tâche récurrente il replanifie, donc deux
 * appels ne valent pas un.
 */
function foldKey(op: QueuedOp): string | null {
  switch (op.kind) {
    case 'task.update':
    case 'course.update':
    case 'habit.update':
    case 'task.reschedule':
      return `${op.kind}:${op.id}`;
    case 'habit.log':
    case 'habit.unlog':
      return `habit.toggle:${op.id}:${op.date}`;
    default:
      return null;
  }
}

/**
 * Le repli **fusionne les champs**, il ne remplace pas l'entrée. Remplacer ferait
 * qu'un renommage suivi d'un changement de date perde le renommage, silencieusement —
 * le pire mode de panne pour une file dont tout l'intérêt est de ne rien perdre.
 */
function fold(previous: QueuedOp, incoming: QueuedOp): QueuedOp {
  if (
    (previous.kind === 'task.update' && incoming.kind === 'task.update') ||
    (previous.kind === 'course.update' && incoming.kind === 'course.update') ||
    (previous.kind === 'habit.update' && incoming.kind === 'habit.update')
  ) {
    return { ...incoming, patch: { ...previous.patch, ...incoming.patch } } as QueuedOp;
  }
  // reschedule (un seul champ) et log/unlog (opposés sur la même date) : le dernier gagne.
  return incoming;
}

/**
 * Ajoute une opération à la file, en appliquant le repli et les annulations.
 *
 * Trois cas au-delà du simple `push` :
 * - supprimer une entité **créée hors-ligne** annule sa création et tout ce qui la vise :
 *   rien n'a atteint le serveur, il n'y a rien à rejouer ;
 * - supprimer une entité **existante** jette d'abord les opérations en attente qui la
 *   visent — sinon le drain les rejoue pour récolter des 404 ;
 * - sinon, repli sur la dernière entrée compatible.
 */
export function enqueueInto(queue: QueueEntry[], op: QueuedOp, now = Date.now()): QueueEntry[] {
  if (DELETE_KINDS.has(op.kind)) {
    const sameEntity = (e: QueueEntry): boolean =>
      e.op.id === op.id && domain(e.op.kind) === domain(op.kind);
    const hadPendingCreate = queue.some((e) => sameEntity(e) && CREATE_KINDS.has(e.op.kind));
    const rest = queue.filter((e) => !sameEntity(e));
    return hadPendingCreate ? rest : [...rest, { entryId: ulid(), at: now, op }];
  }

  const key = foldKey(op);
  if (key === null) return [...queue, { entryId: ulid(), at: now, op }];

  // Pas de `findLastIndex` : il demande la lib ES2023, le tsconfig est en ES2022.
  let lastIndex = -1;
  for (let i = queue.length - 1; i >= 0; i--) {
    if (foldKey(queue[i]!.op) === key) {
      lastIndex = i;
      break;
    }
  }
  if (lastIndex === -1) return [...queue, { entryId: ulid(), at: now, op }];

  const previous = queue[lastIndex]!;
  const merged: QueueEntry = { ...previous, op: fold(previous.op, op) };
  return queue.map((e, i) => (i === lastIndex ? merged : e));
}

/**
 * Réécrit les identifiants temporaires une fois que le serveur a rendu les vrais.
 * Ajouter un article dans le magasin puis le cocher deux rayons plus loin produit
 * exactement cette séquence : c'est le cas normal, pas un cas limite.
 */
export function remapIds(queue: QueueEntry[], map: ReadonlyMap<string, string>): QueueEntry[] {
  if (map.size === 0) return queue;
  return queue.map((e) => {
    const real = map.get(e.op.id);
    return real ? { ...e, op: { ...e.op, id: real } } : e;
  });
}

/** Les entrées encore rattachées à un `tmp_` dont la création a échoué : plus rien ne les porte. */
export function dropOrphansOf(queue: QueueEntry[], tempId: string): QueueEntry[] {
  return queue.filter((e) => e.op.id !== tempId);
}

export function prune(queue: QueueEntry[], now = Date.now(), maxAgeMs = MAX_AGE_MS): {
  kept: QueueEntry[];
  expired: QueueEntry[];
} {
  const kept: QueueEntry[] = [];
  const expired: QueueEntry[] = [];
  for (const e of queue) {
    (now - e.at > maxAgeMs ? expired : kept).push(e);
  }
  return { kept, expired };
}

// ─── Persistance ────────────────────────────────────────────
// localStorage plutôt qu'IndexedDB : mono-utilisateur, quelques dizaines d'entrées
// au pire, et le token d'auth y vit déjà. Toute lecture est défensive — un JSON
// corrompu ne doit pas empêcher l'app de démarrer.

export function load(): QueueEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? adoptLegacy(parsed as QueueEntry[]) : [];
  } catch {
    return [];
  }
}

/**
 * Une entrée mise en file **avant** l'ADR 0011 n'a pas de magasin : elle a été produite
 * quand la liste était unique, donc Carrefour. Sans ça, elle serait rejouée avec le défaut
 * d'écriture (`lafourche`) et l'article atterrirait dans la mauvaise liste, sans un mot —
 * une file hors-ligne survit à une mise à jour de l'app, c'est tout son intérêt.
 *
 * Même raisonnement, et même valeur, que `resolveStore()` côté backend.
 */
export function adoptLegacy(queue: QueueEntry[]): QueueEntry[] {
  return queue.map((e) =>
    e.op.kind === 'course.create' && !e.op.input.store
      ? { ...e, op: { ...e.op, input: { ...e.op.input, store: 'carrefour' as const } } }
      : e
  );
}

export function save(queue: QueueEntry[]): void {
  try {
    if (queue.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Quota plein ou storage bloqué : l'état local reste juste pour cette session,
    // le rejeu ne survivra pas à la fermeture. Rien de mieux à faire ici.
  }
}
